import React, { useState } from 'react';
import type { MenuLanguage } from './types.js';
import type { BotDifficulty } from '../../game/bot-ai.js';
import './bot-match-setup.css';

const MIN_BOTS = 1;
const MAX_BOTS = 7;

const DIFFICULTIES: { value: BotDifficulty; label: (tr: T) => string }[] = [
  { value: 'easy', label: (tr) => tr('Fácil', 'Easy', 'Facile') },
  { value: 'medium', label: (tr) => tr('Normal', 'Normal', 'Normal') },
  { value: 'hard', label: (tr) => tr('Difícil', 'Hard', 'Difficile') },
  { value: 'insane', label: (tr) => tr('Experto', 'Expert', 'Expert') },
];

type T = (es: string, en: string, fr: string) => string;

export function BotMatchSetup({ language, onStart, onBack }: {
  language: MenuLanguage;
  onStart: (botCount: number, difficulty: BotDifficulty) => void;
  onBack: () => void;
}) {
  const es = language === 'es';
  const tr: T = (spanish, english, french) => es ? spanish : language === 'fr' ? french : english;
  const [botCount, setBotCount] = useState(3);
  const [difficulty, setDifficulty] = useState<BotDifficulty>('medium');

  return (
    <main className="bot-match-setup">
      <button className="bot-match-setup__back" onClick={onBack} aria-label={tr('Volver', 'Back', 'Retour')}>
        ←
      </button>
      <div className="bot-match-setup__card">
        <h1 className="bot-match-setup__title">{tr('Contra la máquina', 'Versus machine', 'Contre la machine')}</h1>
        <p className="bot-match-setup__subtitle">
          {tr('Practica contra bots en una arena privada. Sin colas ni recompensas.',
            'Practice against bots in a private arena. No queues or rewards.',
            'Entraînez-vous contre des bots dans une arène privée. Sans files ni récompenses.')}
        </p>

        <section className="bot-match-setup__group">
          <div className="bot-match-setup__label-row">
            <label htmlFor="bot-count">{tr('Número de bots', 'Number of bots', 'Nombre de bots')}</label>
            <span className="bot-match-setup__value">{botCount}</span>
          </div>
          <div className="bot-match-setup__stepper">
            <button
              type="button"
              className="bot-match-setup__step-btn"
              onClick={() => setBotCount((current) => Math.max(MIN_BOTS, current - 1))}
              disabled={botCount <= MIN_BOTS}
              aria-label={tr('Menos bots', 'Fewer bots', 'Moins de bots')}
            >−</button>
            <input
              id="bot-count"
              type="range"
              min={MIN_BOTS}
              max={MAX_BOTS}
              step={1}
              value={botCount}
              onChange={(event) => setBotCount(Number(event.target.value))}
              aria-valuetext={`${botCount}`}
            />
            <button
              type="button"
              className="bot-match-setup__step-btn"
              onClick={() => setBotCount((current) => Math.min(MAX_BOTS, current + 1))}
              disabled={botCount >= MAX_BOTS}
              aria-label={tr('Más bots', 'More bots', 'Plus de bots')}
            >+</button>
          </div>
        </section>

        <section className="bot-match-setup__group">
          <div className="bot-match-setup__label-row">
            <span>{tr('Dificultad', 'Difficulty', 'Difficulté')}</span>
          </div>
          <div className="bot-match-setup__difficulties">
            {DIFFICULTIES.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={`bot-match-setup__difficulty${difficulty === value ? ' is-active' : ''}`}
                onClick={() => setDifficulty(value)}
              >
                {label(tr)}
              </button>
            ))}
          </div>
        </section>

        <button
          className="bot-match-setup__start"
          onClick={() => onStart(botCount, difficulty)}
        >
          {tr('EMPEZAR PARTIDA', 'START MATCH', 'LANCER LA PARTIE')}
        </button>
      </div>
    </main>
  );
}